import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, wpImg } from "@/lib/api";
import {
  Calendar, Users, CheckCircle2, Loader2, Mail, Phone, MapPin, ArrowRight, Sparkles, Building,
  Plus, Minus, Calculator, ChevronLeft, ChevronRight, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";
import MarkdownView from "@/components/MarkdownView";

// ---- helpers ----
function parsePrice(str) {
  if (str == null) return 0;
  const m = String(str).replace(/,/g, "").match(/-?\d+(?:\.\d+)?/);
  return m ? parseFloat(m[0]) : 0;
}
function fmtMoney(n) {
  return `$${(Math.round(n * 100) / 100).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function FacilityRentals() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", halls: [],
    event_date: "", expected_guests: "", event_type: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [addonQty, setAddonQty] = useState({});
  const [addonsOpen, setAddonsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("rules"); // rules first — so visitors see T&Cs before booking
  const inquiryRef = useRef(null);

  useEffect(() => {
    api.get("/facility-rentals")
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const toggleHall = (name) => {
    setForm((f) => ({
      ...f,
      halls: f.halls.includes(name) ? f.halls.filter((h) => h !== name) : [...f.halls, name],
    }));
  };

  const scrollToInquiry = () => {
    inquiryRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.event_type.trim() ||
        !form.event_date || !form.expected_guests || !form.message.trim() || form.halls.length === 0) {
      return toast.error("Please complete every field — all are required.");
    }
    setSubmitting(true);
    try {
      const _halls = (data?.halls || []);
      const _addons = (data?.addons || []);
      const _hallLines = form.halls.map((n) => {
        const h = _halls.find((x) => x.name === n);
        return { name: n, price: parsePrice(h?.price) };
      });
      const _addonLines = _addons
        .map((a) => ({ name: a.name, unit: parsePrice(a.price), qty: addonQty[a.name] || 0 }))
        .filter((l) => l.qty > 0)
        .map((l) => ({ ...l, subtotal: l.unit * l.qty }));
      const _hallSubtotal = _hallLines.reduce((s, l) => s + l.price, 0);
      const _addonSubtotal = _addonLines.reduce((s, l) => s + l.subtotal, 0);
      const _total = _hallSubtotal + _addonSubtotal;

      let augmentedMsg = form.message.trim();
      const lines = [];
      if (_hallLines.length > 0) {
        lines.push("Halls requested:");
        _hallLines.forEach((l) => lines.push(`  • ${l.name} — ${fmtMoney(l.price)}`));
      }
      if (_addonLines.length > 0) {
        lines.push("Add-ons:");
        _addonLines.forEach((l) => lines.push(`  • ${l.name} × ${l.qty} = ${fmtMoney(l.subtotal)}`));
      }
      if (lines.length > 0) {
        lines.push(`Estimated total: ${fmtMoney(_total)} (preview — final quote pending)`);
        augmentedMsg = `${augmentedMsg}\n\n— — —\n${lines.join("\n")}`;
      }
      const payload = { ...form, expected_guests: Number(form.expected_guests), message: augmentedMsg };
      await api.post("/facility-rentals/inquiry", payload);
      setSubmitted(true);
      toast.success("Inquiry sent — Dr. Tarakji will be in touch, inshaAllah.");
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Something went wrong");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-gold" /></div>;
  if (!data) return <div className="py-20 text-center text-ink/50">Content coming soon.</div>;

  const halls = (data.halls || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const addons = (data.addons || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const steps = (data.process_steps || []).sort((a, b) => (a.order || 0) - (b.order || 0));
  const gallery = data.gallery_images || [];

  // ---- live estimate ----
  const setQty = (name, q) => setAddonQty((p) => ({ ...p, [name]: Math.max(0, q | 0) }));
  const hallLines = form.halls.map((name) => {
    const h = halls.find((x) => x.name === name);
    return { name, price: parsePrice(h?.price) };
  });
  const addonLines = addons
    .map((a) => ({ name: a.name, unit: parsePrice(a.price), qty: addonQty[a.name] || 0 }))
    .filter((l) => l.qty > 0)
    .map((l) => ({ ...l, subtotal: l.unit * l.qty }));
  const hallSubtotal = hallLines.reduce((s, l) => s + l.price, 0);
  const addonSubtotal = addonLines.reduce((s, l) => s + l.subtotal, 0);
  const total = hallSubtotal + addonSubtotal;
  const hasSelection = form.halls.length > 0 || addonLines.length > 0;

  // Build tabs only for sections that exist. Rules first to encourage
  // visitors to read T&Cs before jumping into booking.
  const tabs = [
    data.rules_markdown && { id: "rules", label: "Rules" },
    steps.length > 0 && { id: "process", label: "How it works" },
    gallery.length > 0 && { id: "gallery", label: "Gallery" },
    data.intro_markdown && { id: "about", label: "About" },
  ].filter(Boolean);
  // Make sure the active tab is one that exists
  const safeActive = tabs.find((t) => t.id === activeTab) ? activeTab : (tabs[0]?.id || "process");

  return (
    <div data-testid="facility-rentals-page" className="pb-24 lg:pb-0">
      {/* COMPACT HERO — 38vh on mobile, 52vh on desktop. Was 100vh+. */}
      <section className="relative overflow-hidden border-b border-sand">
        {data.hero_image ? (
          <>
            <div className="absolute inset-0">
              <img src={data.hero_image} alt="" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-navy/95 via-navy/80 to-navy/55" />
            </div>
          </>
        ) : (
          <div className="absolute inset-0 bg-navy pattern-girih opacity-50" />
        )}
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20 text-cream">
          <div className="text-[10px] uppercase tracking-[0.35em] text-gold font-semibold inline-flex items-center gap-2">
            <Sparkles size={12} /> {data.hero_eyebrow}
          </div>
          <h1 className="display text-4xl md:text-5xl lg:text-6xl mt-4 leading-[1.05] max-w-2xl">
            {data.hero_title}
          </h1>
          {data.hero_subtitle && (
            <div className="mt-3 font-serif text-lg md:text-xl text-gold max-w-xl">{data.hero_subtitle}</div>
          )}
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={scrollToInquiry}
              data-testid="hero-inquiry-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy px-7 py-3.5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-gold-600 shadow-lg shadow-gold/30 transition-all hover:-translate-y-0.5"
            >
              Request a booking <ArrowRight size={14} />
            </button>
            <a
              href="#halls"
              className="inline-flex items-center gap-2 rounded-full border-2 border-cream/30 text-cream px-7 py-3 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-cream/10 transition-colors"
            >
              Browse halls
            </a>
          </div>
        </div>
      </section>

      {/* MAIN BODY — desktop two-column, mobile linear */}
      <section className="relative bg-cream">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-10 md:py-14">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12">
            {/* LEFT — tabs FIRST (rules before booking), then halls slider, then add-ons */}
            <div className="lg:col-span-7 min-w-0 space-y-10">
              {/* TABS — Process / Rules / Gallery / About — on top so visitors
                  can read the rules and "how it works" before booking. */}
              {tabs.length > 0 && (
                <div className="rounded-3xl bg-white border border-sand overflow-hidden">
                  <div className="border-b border-sand px-2 md:px-4 flex gap-1 md:gap-2 overflow-x-auto admin-scroll" data-testid="info-tabs">
                    {tabs.map((t) => (
                      <button
                        key={t.id}
                        type="button"
                        onClick={() => setActiveTab(t.id)}
                        data-testid={`tab-${t.id}`}
                        className={`shrink-0 px-4 md:px-5 py-3 text-xs uppercase tracking-[0.18em] font-semibold border-b-2 -mb-px transition-colors ${
                          safeActive === t.id ? "border-gold text-navy" : "border-transparent text-ink/55 hover:text-navy"
                        }`}
                      >
                        {t.label}
                      </button>
                    ))}
                  </div>
                  <div className="p-5 md:p-7">
                    {safeActive === "process" && steps.length > 0 && (
                      <div className="grid sm:grid-cols-2 gap-3" data-testid="tab-content-process">
                        {steps.map((s, i) => (
                          <div key={i} className="rounded-2xl bg-cream/60 border border-sand p-4">
                            <div className="w-8 h-8 rounded-full bg-gold text-navy flex items-center justify-center font-bold font-serif text-sm">{i + 1}</div>
                            <div className="font-serif text-base mt-2 text-ink leading-tight">{s.title}</div>
                            <div className="text-xs text-ink/65 mt-1.5 leading-relaxed">{s.description}</div>
                          </div>
                        ))}
                      </div>
                    )}
                    {safeActive === "rules" && data.rules_markdown && (
                      <div className="markdown-page text-sm" data-testid="tab-content-rules">
                        <MarkdownView>{data.rules_markdown}</MarkdownView>
                      </div>
                    )}
                    {safeActive === "gallery" && gallery.length > 0 && (
                      <div data-testid="tab-content-gallery">
                        <div className="columns-2 md:columns-3 gap-3">
                          {gallery.slice(0, 6).map((src, i) => (
                            <img key={i} src={wpImg(src)} alt={`Facility ${i + 1}`} loading="lazy"
                              className="w-full h-auto mb-3 rounded-xl break-inside-avoid" />
                          ))}
                        </div>
                        {gallery.length > 6 && (
                          <Link to="/gallery" className="mt-4 inline-flex items-center gap-1.5 text-sm text-navy hover:gap-2.5 transition-all">
                            View full gallery <ArrowRight size={14} />
                          </Link>
                        )}
                      </div>
                    )}
                    {safeActive === "about" && data.intro_markdown && (
                      <div className="markdown-page text-sm" data-testid="tab-content-about">
                        <MarkdownView>{data.intro_markdown}</MarkdownView>
                      </div>
                    )}
                  </div>
                  <div className="px-5 md:px-7 pb-5 md:pb-6 pt-1 flex items-center justify-between gap-3 text-xs text-ink/55 border-t border-sand/60 mt-1">
                    <span>Got it? Pick a hall below to continue.</span>
                    <a
                      href="#halls"
                      onClick={(e) => { e.preventDefault(); document.getElementById("halls")?.scrollIntoView({ behavior: "smooth", block: "start" }); }}
                      data-testid="tabs-continue-to-halls"
                      className="inline-flex items-center gap-1.5 text-navy font-semibold hover:gap-2 transition-all"
                    >
                      Browse halls <ArrowRight size={12} />
                    </a>
                  </div>
                </div>
              )}

              {/* HALLS SLIDER */}
              <div id="halls">
                <div className="flex items-end justify-between gap-4 mb-5">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Spaces for rent</div>
                    <h2 className="font-serif text-3xl md:text-4xl text-ink mt-2">Halls & pricing</h2>
                  </div>
                  <div className="text-xs text-ink/55 hidden md:block">Tap a hall to add it to your inquiry →</div>
                </div>
                <HallsCarousel
                  halls={halls}
                  selectedNames={form.halls}
                  onSelect={(name) => { toggleHall(name); }}
                />
              </div>

              {/* ADD-ONS — collapsed accordion */}
              {addons.length > 0 && (
                <div className="rounded-3xl bg-white border border-sand overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setAddonsOpen((v) => !v)}
                    data-testid="addons-toggle"
                    className="w-full flex items-center justify-between gap-4 p-5 md:p-6 hover:bg-cream/40 transition-colors"
                    aria-expanded={addonsOpen}
                  >
                    <div className="text-left">
                      <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Optional add-ons</div>
                      <div className="font-serif text-xl text-ink mt-1">
                        Loveseats, dinnerware & more
                        {Object.values(addonQty).some((q) => q > 0) && (
                          <span className="ml-2 text-xs text-navy font-sans align-middle bg-gold/20 rounded-full px-2 py-0.5">
                            {Object.values(addonQty).reduce((s, q) => s + (q > 0 ? 1 : 0), 0)} added
                          </span>
                        )}
                      </div>
                    </div>
                    {addonsOpen ? <ChevronUp size={18} className="text-navy shrink-0" /> : <ChevronDown size={18} className="text-navy shrink-0" />}
                  </button>
                  {addonsOpen && (
                    <div className="border-t border-sand p-5 md:p-6 space-y-3">
                      {addons.map((a, i) => {
                        const qty = addonQty[a.name] || 0;
                        const unit = parsePrice(a.price);
                        return (
                          <div
                            key={i}
                            data-testid={`addon-row-${i}`}
                            className={`flex items-start justify-between gap-3 p-3.5 rounded-xl border ${qty > 0 ? "bg-gold/10 border-gold/40" : "bg-cream border-sand"}`}
                          >
                            {a.image_url && (
                              <img
                                src={wpImg(a.image_url)}
                                alt=""
                                loading="lazy"
                                className="w-16 h-16 md:w-20 md:h-20 rounded-lg object-cover shrink-0 border border-sand"
                              />
                            )}
                            <div className="min-w-0 flex-1">
                              <div className="font-serif text-base text-ink">{a.name}</div>
                              {a.features?.length > 0 && (
                                <ul className="mt-1 text-[11px] text-ink/55 leading-snug">
                                  {a.features.map((f, j) => <li key={j}>· {f}</li>)}
                                </ul>
                              )}
                              <div className="mt-1 text-[11px] text-ink/55">Unit: <span className="text-navy font-semibold">{a.price}</span></div>
                            </div>
                            <div className="shrink-0 flex flex-col items-end gap-1.5">
                              <div className="inline-flex items-center rounded-full border border-sand bg-white overflow-hidden" data-testid={`addon-qty-${i}`}>
                                <button type="button" onClick={() => setQty(a.name, qty - 1)} disabled={qty === 0}
                                  className="w-8 h-8 flex items-center justify-center text-navy hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed">
                                  <Minus size={12} />
                                </button>
                                <input
                                  type="number"
                                  min="0"
                                  value={qty}
                                  onChange={(e) => setQty(a.name, parseInt(e.target.value, 10) || 0)}
                                  data-testid={`addon-qty-input-${i}`}
                                  className="w-10 text-center text-sm font-semibold text-ink bg-transparent border-x border-sand focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                                />
                                <button type="button" onClick={() => setQty(a.name, qty + 1)}
                                  className="w-8 h-8 flex items-center justify-center text-navy hover:bg-cream">
                                  <Plus size={12} />
                                </button>
                              </div>
                              {qty > 0 && (
                                <div className="text-[11px] text-navy font-semibold">{fmtMoney(unit * qty)}</div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT — sticky inquiry form + estimate (col-5 on lg) */}
            <div className="lg:col-span-5 min-w-0">
              <div className="lg:sticky lg:top-24 space-y-4" ref={inquiryRef} id="inquiry">
                {/* LIVE ESTIMATE — only shown when something is selected */}
                {hasSelection && (
                  <div data-testid="estimate-panel" className="rounded-3xl overflow-hidden bg-gradient-to-br from-navy to-navy-900 text-cream relative">
                    <div className="absolute inset-0 pattern-girih opacity-15" />
                    <div className="relative p-5 md:p-6">
                      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
                        <div className="inline-flex items-center gap-2">
                          <span className="w-8 h-8 rounded-full bg-gold text-navy flex items-center justify-center"><Calculator size={14} /></span>
                          <div>
                            <div className="text-[9px] uppercase tracking-[0.28em] text-gold font-semibold">Live estimate</div>
                            <div className="font-serif text-base">Your selection</div>
                          </div>
                        </div>
                        <button onClick={() => { setForm((f) => ({ ...f, halls: [] })); setAddonQty({}); }}
                          data-testid="estimate-reset"
                          className="text-[10px] uppercase tracking-wider text-cream/70 hover:text-gold underline-offset-4 hover:underline">
                          Reset
                        </button>
                      </div>

                      <div className="space-y-1 text-sm max-h-32 overflow-y-auto pr-1">
                        {hallLines.map((l) => (
                          <div key={l.name} className="flex justify-between border-b border-cream/10 py-1.5">
                            <span className="text-cream/85 text-xs">{l.name}</span>
                            <span className="font-mono text-cream text-xs">{fmtMoney(l.price)}</span>
                          </div>
                        ))}
                        {addonLines.map((l) => (
                          <div key={l.name} className="flex justify-between border-b border-cream/10 py-1.5">
                            <span className="text-cream/85 text-xs">{l.name} <span className="text-cream/55">× {l.qty}</span></span>
                            <span className="font-mono text-cream text-xs">{fmtMoney(l.subtotal)}</span>
                          </div>
                        ))}
                      </div>

                      <div className="mt-4 pt-4 border-t border-gold/30 flex items-end justify-between gap-3">
                        <div>
                          <div className="text-[9px] uppercase tracking-[0.25em] text-gold font-semibold">Estimated total</div>
                          <div data-testid="estimate-total" className="font-serif text-3xl text-cream mt-0.5 leading-none tabular-nums">{fmtMoney(total)}</div>
                        </div>
                      </div>
                      <p className="mt-3 text-[10px] text-cream/55 leading-relaxed">
                        Estimate only — final quote confirmed after a site visit.
                      </p>
                    </div>
                  </div>
                )}

                {/* INQUIRY FORM */}
                {data.inquiry_enabled && (
                  <div className="rounded-3xl bg-white border-2 border-gold/40 shadow-2xl shadow-navy/10 overflow-hidden">
                    <div className="bg-gradient-to-br from-navy to-navy-900 text-cream p-5 md:p-6 relative overflow-hidden">
                      <div className="absolute inset-0 pattern-girih opacity-15" />
                      <div className="relative">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-gold font-semibold">Booking inquiry</div>
                        <h2 className="font-serif text-2xl md:text-3xl mt-1.5 leading-tight">Tell us about your event</h2>
                        <p className="text-cream/70 text-xs mt-2 leading-relaxed">{data.inquiry_disclaimer}</p>
                      </div>
                    </div>

                    {submitted ? (
                      <div className="p-8 text-center">
                        <CheckCircle2 className="mx-auto text-gold" size={40} />
                        <h3 className="font-serif text-xl text-ink mt-4">Your inquiry is on its way</h3>
                        <p className="text-ink/60 mt-2 text-sm">Dr. Tarakji will review and reach out directly — inshaAllah within a few days.</p>
                        <button
                          onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", halls: [], event_date: "", expected_guests: "", event_type: "", message: "" }); }}
                          className="mt-5 text-navy underline text-sm"
                        >
                          Send another inquiry
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={submit} data-testid="inquiry-form" className="p-5 md:p-6 space-y-4">
                        <div className="text-[10px] text-ink/50">All fields are required.</div>
                        <Field label="Your name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="inq-name" required />
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="inq-email" required />
                          <Field label="Phone *" type="tel" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testId="inq-phone" required />
                        </div>
                        <Field label="Event type (wedding, lecture…) *" value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} testId="inq-event-type" required />

                        <div>
                          <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold flex items-center gap-1">
                            <Building size={10} /> Hall(s) *
                          </label>
                          {form.halls.length === 0 ? (
                            <div className="mt-2 rounded-xl border border-dashed border-gold/50 bg-gold/5 px-3 py-2.5 text-xs text-ink/65">
                              <span className="font-semibold text-navy">Pick a hall</span> from the slider on the left to continue.
                            </div>
                          ) : (
                            <div className="mt-2 flex flex-wrap gap-1.5" data-testid="inq-halls-pills">
                              {form.halls.map((h) => (
                                <span key={h} data-testid={`inq-hall-pill-${h.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                  className="inline-flex items-center gap-1.5 bg-navy text-cream px-3 py-1 rounded-full text-xs">
                                  {h}
                                  <button type="button" onClick={() => toggleHall(h)} className="text-cream/70 hover:text-gold leading-none">×</button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold flex items-center gap-1"><Calendar size={10} /> Date *</label>
                            <input type="date" required value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} data-testid="inq-date" className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-sand focus:outline-none focus:border-navy bg-white text-sm" />
                          </div>
                          <div>
                            <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold flex items-center gap-1"><Users size={10} /> Guests *</label>
                            <input type="number" min="1" required value={form.expected_guests} onChange={(e) => setForm({ ...form, expected_guests: e.target.value })} data-testid="inq-guests" className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm" />
                          </div>
                        </div>

                        <div>
                          <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">Anything else? *</label>
                          <textarea rows={3} required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="inq-message" className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-sand focus:outline-none focus:border-navy resize-none text-sm" placeholder="Tell us about your event…" />
                        </div>

                        <button type="submit" disabled={submitting} data-testid="inq-submit" className="w-full rounded-full bg-gold text-navy py-3.5 text-xs font-bold uppercase tracking-[0.25em] hover:bg-gold-600 disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-lg shadow-gold/30 transition-all hover:-translate-y-0.5">
                          {submitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
                          Send inquiry
                        </button>
                        <p className="text-[10px] text-ink/55 text-center leading-relaxed">
                          Forwarded directly to Dr. Tarakji (CC FIC management). Reply within a few days.
                        </p>
                      </form>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT STRIP */}
      <section className="bg-navy text-cream px-6 md:px-10 py-10 border-t border-gold/15 relative overflow-hidden">
        <div className="absolute inset-0 pattern-girih opacity-10" />
        <div className="relative max-w-5xl mx-auto grid md:grid-cols-3 gap-6 text-sm">
          <div className="flex gap-3"><MapPin className="text-gold shrink-0" size={18} /><div><div className="text-[10px] uppercase tracking-[0.2em] text-gold">Address</div><div className="mt-1">9447 Corunna Rd<br/>Swartz Creek, MI 48473</div></div></div>
          <div className="flex gap-3"><Phone className="text-gold shrink-0" size={18} /><div><div className="text-[10px] uppercase tracking-[0.2em] text-gold">Call us</div><a href="tel:+18104062617" className="mt-1 block hover:text-gold">(810) 406-2617</a></div></div>
          <div className="flex gap-3"><Mail className="text-gold shrink-0" size={18} /><div><div className="text-[10px] uppercase tracking-[0.2em] text-gold">Email</div><a href="mailto:flintislamiccenter@gmail.com" className="mt-1 block hover:text-gold break-all">flintislamiccenter@gmail.com</a></div></div>
        </div>
      </section>

      {/* MOBILE STICKY CTA — visible only on lg-down. Persistent "Send inquiry" button so visitors never need to scroll-hunt. */}
      {!submitted && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-cream/95 backdrop-blur border-t border-sand shadow-[0_-8px_24px_rgba(14,42,71,0.12)]">
          <div className="px-4 py-3 flex items-center gap-3">
            <div className="flex-1 min-w-0">
              {hasSelection ? (
                <>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-gold font-semibold leading-none">Estimated total</div>
                  <div className="font-serif text-xl text-navy leading-none mt-1 tabular-nums">{fmtMoney(total)}</div>
                </>
              ) : (
                <>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-gold font-semibold leading-none">Ready to book?</div>
                  <div className="font-serif text-base text-navy leading-tight mt-1">Send an inquiry</div>
                </>
              )}
            </div>
            <button
              type="button"
              onClick={scrollToInquiry}
              data-testid="mobile-cta-inquiry"
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-gold text-navy px-5 py-3 text-xs font-bold uppercase tracking-[0.18em] hover:bg-gold-600 shadow-lg shadow-gold/40"
            >
              {hasSelection ? "Continue" : "Request"} <ArrowRight size={13} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- HALLS CAROUSEL ----------
function HallsCarousel({ halls, selectedNames, onSelect }) {
  const trackRef = useRef(null);
  const [idx, setIdx] = useState(0);

  const scrollTo = (i) => {
    const track = trackRef.current;
    if (!track) return;
    const child = track.children[i];
    if (!child) return;
    track.scrollTo({ left: child.offsetLeft - track.offsetLeft, behavior: "smooth" });
    setIdx(i);
  };

  const onScroll = () => {
    const track = trackRef.current;
    if (!track) return;
    const cw = track.clientWidth;
    const i = Math.round(track.scrollLeft / cw);
    setIdx(Math.max(0, Math.min(halls.length - 1, i)));
  };

  if (halls.length === 0) return null;

  return (
    <div className="relative" data-testid="halls-carousel">
      {/* Arrows — desktop only */}
      {halls.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollTo(Math.max(0, idx - 1))}
            disabled={idx === 0}
            aria-label="Previous hall"
            className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-3 z-10 w-10 h-10 rounded-full bg-white border border-sand text-navy shadow-lg items-center justify-center hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            onClick={() => scrollTo(Math.min(halls.length - 1, idx + 1))}
            disabled={idx === halls.length - 1}
            aria-label="Next hall"
            className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-3 z-10 w-10 h-10 rounded-full bg-white border border-sand text-navy shadow-lg items-center justify-center hover:bg-cream disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ChevronRight size={18} />
          </button>
        </>
      )}

      <div
        ref={trackRef}
        onScroll={onScroll}
        className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-1 px-1 scroll-smooth admin-scroll"
        style={{ scrollbarWidth: "thin" }}
      >
        {halls.map((h, i) => (
          <div key={i} className="snap-center shrink-0 w-full md:w-[calc(100%-2px)]">
            <HallCard
              hall={h}
              selected={selectedNames.includes(h.name)}
              onSelect={() => onSelect(h.name)}
            />
          </div>
        ))}
      </div>

      {/* Dots */}
      {halls.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-2" data-testid="halls-dots">
          {halls.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Go to hall ${i + 1}`}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-navy" : "w-1.5 bg-navy/25 hover:bg-navy/45"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function HallCard({ hall, selected, onSelect }) {
  const [imgLoaded, setImgLoaded] = useState(false);
  const showImage = !!hall.image_url;
  return (
    <div className={`rounded-3xl overflow-hidden bg-white border transition-all flex flex-col md:flex-row ${selected ? "border-navy ring-2 ring-gold/40 shadow-2xl shadow-navy/15" : "border-sand hover:shadow-2xl hover:shadow-navy/10"}`}>
      <div className="md:w-1/2 aspect-[4/3] md:aspect-auto bg-navy relative overflow-hidden">
        {/* Always-on Islamic backdrop so the photo box never feels empty */}
        <div className="absolute inset-0 pattern-girih opacity-[0.18]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(197,160,89,0.25)_0%,transparent_70%)]" />
        {/* Mosque silhouette while the photo loads or if it fails */}
        {!imgLoaded && (
          <div className="absolute inset-0 flex items-center justify-center px-6">
            <svg viewBox="0 0 220 140" className="w-32 md:w-44 h-auto" fill="none" aria-hidden="true">
              <g stroke="#C5A059" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="20" y1="120" x2="20" y2="50" strokeOpacity="0.85" />
                <path d="M14 50 L20 38 L26 50 Z" strokeOpacity="0.85" fill="#C5A059" fillOpacity="0.18" />
                <circle cx="20" cy="32" r="2.5" strokeOpacity="0.85" />
                <path d="M55 120 L55 80 Q55 50 110 50 Q165 50 165 80 L165 120 Z" strokeOpacity="0.95" fill="#C5A059" fillOpacity="0.10" />
                <circle cx="110" cy="42" r="3.5" strokeOpacity="0.95" fill="#C5A059" fillOpacity="0.4" />
                <line x1="110" y1="20" x2="110" y2="38" strokeOpacity="0.85" />
                <path d="M95 120 L95 100 Q110 86 125 100 L125 120" strokeOpacity="0.85" fill="#0E2A47" />
                <line x1="200" y1="120" x2="200" y2="50" strokeOpacity="0.85" />
                <path d="M194 50 L200 38 L206 50 Z" strokeOpacity="0.85" fill="#C5A059" fillOpacity="0.18" />
                <circle cx="200" cy="32" r="2.5" strokeOpacity="0.85" />
                <line x1="0" y1="120" x2="220" y2="120" strokeOpacity="0.5" />
              </g>
            </svg>
          </div>
        )}
        {showImage && (
          <img
            src={wpImg(hall.image_url)}
            alt={hall.name}
            onLoad={(e) => { if (e.currentTarget.naturalWidth > 0) setImgLoaded(true); }}
            onError={() => setImgLoaded(false)}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${imgLoaded ? "opacity-100" : "opacity-0"}`}
          />
        )}
        <div className="absolute top-3 right-3 rounded-full bg-navy/90 text-gold px-3 py-1 text-[10px] uppercase tracking-wider font-semibold backdrop-blur z-10">
          {hall.capacity} guests
        </div>
        {selected && (
          <div className="absolute top-3 left-3 rounded-full bg-gold text-navy px-3 py-1 text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1 z-10">
            <CheckCircle2 size={11} /> Added
          </div>
        )}
      </div>
      <div className="p-5 md:p-6 flex-1 flex flex-col">
        <h3 className="font-serif text-xl md:text-2xl text-ink leading-tight">{hall.name}</h3>
        <div className="mt-1.5 font-serif text-2xl md:text-3xl text-navy">{hall.price}</div>
        {hall.features?.length > 0 && (
          <ul className="mt-3 space-y-1 text-xs text-ink/70 flex-1">
            {hall.features.slice(0, 4).map((f, i) => (
              <li key={i} className="flex items-start gap-1.5">
                <CheckCircle2 size={12} className="text-gold shrink-0 mt-0.5" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
        <button
          type="button"
          onClick={onSelect}
          data-testid={`hall-toggle-${hall.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
          className={`mt-4 w-full rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
            selected ? "bg-gold text-navy hover:bg-gold-600" : "bg-navy text-cream hover:bg-navy-900"
          }`}
        >
          {selected ? <>Remove from inquiry</> : <>Add to inquiry <ArrowRight size={12} /></>}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId, required = false }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.22em] text-gold font-semibold">{label}</label>
      <input
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="mt-1.5 w-full px-3 py-2.5 rounded-xl border border-sand focus:outline-none focus:border-navy text-sm"
      />
    </div>
  );
}
